import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, FormControl, FormField, FormItem, FormMessage, Label, Tabs, TabsContent, TabsList, TabsTrigger, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useTerminalClient } from '../client-rpc-router';
import { HandlingUnitScanInput } from '../components/HandlingUnitScanInput';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';

const formSchema = z.object({
	handlingUnit: z
		.object({
			id: z.number(),
			code: z.string(),
			storageLocation: z
				.object({
					name: z.string().optional()
				})
				.optional()
		})
		.nullable()
		.optional(),
	destinationLocation: z
		.object({
			id: z.number(),
			name: z.string().optional()
		})
		.nullable()
		.optional()
});

type FormValues = z.infer<typeof formSchema>;

function HandlingUnitPreview() {
	const form = useFormContext<FormValues>();
	const handlingUnit = useWatch({ control: form.control, name: 'handlingUnit' });

	return (
		<div className="border border-input-border rounded-md">
			<div className="flex flex-row p-3">
				<div className="font-semibold w-1/2">Číslo palety</div>
				<div className="w-1/2">{handlingUnit?.code}</div>
			</div>
			<div className="flex flex-row p-3 border-t border-input-border">
				<div className="font-semibold w-1/2">Lokace</div>
				<div className="w-1/2">{handlingUnit?.storageLocation?.name}</div>
			</div>
		</div>
	);
}

export default function MoveHandlingUnitPage() {
	const client = useTerminalClient();
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<'sourceHandlingUnit' | 'loaded'>('sourceHandlingUnit');
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			handlingUnit: null,
			destinationLocation: null
		}
	});

	const loadMutation = useMutation({
		mutationFn: async (values: FormValues) => {
			await client.warehouseMove.processMoveLoadMutation.execute({
				type: 'Palette',
				items: [
					{
						paletteId: values.handlingUnit?.id as number | undefined,
						stockItemId: 0,
						quantity: 1
					}
				]
			});
		},
		onSuccess: () => {
			setActiveTab('loaded');
			loadedHandlingUnitsQuery.refetch();
		}
	});

	const unloadMutation = useMutation({
		mutationFn: async (values: FormValues) => {
			await client.warehouseMove.processMoveUnloadMutation.execute({
				type: 'Palette',
				destinationLocationId: values.destinationLocation?.id as number | undefined
			});
		},
		onSuccess: () => {
			toast.success('Paleta byla naložena');
			navigate('/move');
		}
	});

	const loadedHandlingUnitsQuery = useQuery({
		queryKey: ['warehouse-move-loaded-handling-units'],
		queryFn: () => client.warehouseMove.getLoadedHandlingUnits.execute({})
	});

	useValidationResultDialog([loadMutation.error, unloadMutation.error]);

	const handleLoadSubmit = form.handleSubmit(values => {
		form.setValue('handlingUnit', null);
		loadMutation.mutate(values);
	});

	const handleUnloadSubmit = form.handleSubmit(values => {
		unloadMutation.mutate(values);
	});

	return (
		<TerminalScreen showBackButton backButtonTo="/move" topBar={<div>Přesun LP</div>} bottomContent={<div ref={setBottomElementRef} />}>
			<Form {...form}>
				<div className="p-2 flex flex-col gap-4">
					<Tabs value={activeTab} onValueChange={v => setActiveTab(v)} className="flex-1">
						<TabsContent value="sourceHandlingUnit" className="space-y-3">
							<Label className="mb-1 block">Zdrojová paleta</Label>
							<FormField
								control={form.control}
								name="handlingUnit"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<HandlingUnitScanInput
												placeholder="Naskenujte klec..."
												value={field.value ?? null}
												onChange={value => {
													field.onChange(value);
													loadedHandlingUnitsQuery.refetch();
												}}
												autoFocus
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<HandlingUnitPreview />

							<Button onClick={handleLoadSubmit} disabled={loadMutation.isPending} className="w-full mt-2">
								Naložit paletu
							</Button>
						</TabsContent>

						<TabsContent value="loaded" className="space-y-5">
							<Label className="mb-1 block">Cílová lokace</Label>
							<FormField
								control={form.control}
								name="destinationLocation"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<StorageLocationScanInput placeholder="Naskenujte lokaci..." value={field.value ?? null} onChange={field.onChange} autoFocus />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<Label className="mb-1 block">Naložené palety</Label>
							<div className="mt-2 space-y-2 divide-y gap-2 p-2 rounded-md border border-input-border">
								{loadedHandlingUnitsQuery.data?.map(handlingUnit => (
									<div key={handlingUnit.code} className="font-bold text-md">
										{handlingUnit.code}
									</div>
								))}
							</div>

							<Button onClick={handleUnloadSubmit} disabled={unloadMutation.isPending} className="w-full mt-2">
								Vyložit paletu
							</Button>
						</TabsContent>

						{bottomElementRef &&
							createPortal(
								<TabsList className="grid grid-cols-2 w-full">
									<TabsTrigger value="sourceHandlingUnit" className="w-full">
										Výběr palety
									</TabsTrigger>
									<TabsTrigger value="loaded" className="w-full">
										Naložené
									</TabsTrigger>
								</TabsList>,
								bottomElementRef
							)}
					</Tabs>
				</div>
			</Form>
		</TerminalScreen>
	);
}
